import type { AssetManifest } from "../assets/manifest.ts";
import type { MapDocument } from "../document/types.ts";
import { measureDensity, type DensityDefinition, type DensityPoint, type DensityReading } from "./density.ts";
import type { EntityTypeDefinition } from "./schema.ts";

/**
 * The density meter as the store holds it (ADR-088 decisions 7 and 8): whether the Density drawer is open,
 * the point measured (the view's centre as the page last saw the camera settle, or a point a press pinned), a
 * pick waiting for that press, and the reading. View state: never saved, compiled or undone, and closed on every
 * load. The reading is computed when read and kept until the map document, the centre or the manifest changes,
 * so a render or a repaint computes nothing.
 */

/**
 * What the meter reads from the store, each read when a reading is asked for. `lowered` is the open map with
 * its followed cells lowered; `definition` and `view` are the game's registration and camera view, null when it
 * registers none.
 */
export interface DensityMeterHost {
  readonly lowered: () => MapDocument;
  readonly tileSize: () => number;
  readonly chunkSize: () => number;
  readonly cells: (layerId: string) => ArrayLike<number> | null;
  readonly entityTypes: () => readonly EntityTypeDefinition[];
  readonly definition: () => DensityDefinition | null;
  readonly view: () => { readonly width: number; readonly height: number } | null;
  readonly notify: () => void;
}

/**
 * The meter.
 */
export class DensityMeter {
  private shown = false;
  private waiting = false;
  private viewCentre: { readonly mapId: string; readonly point: DensityPoint } | null = null;
  private pin: { readonly mapId: string; readonly point: DensityPoint } | null = null;
  private manifest: AssetManifest | null = null;
  private memo: { readonly map: MapDocument; readonly centre: DensityPoint; readonly manifest: AssetManifest; readonly reading: DensityReading } | null = null;

  /**
   * @param host The store's side.
   */
  public constructor(private readonly host: DensityMeterHost) {}

  /**
   * Whether the game registers a density and a camera, without which the editor offers no meter.
   *
   * @returns True when it does.
   */
  public get offered(): boolean {
    return this.host.definition() !== null && this.host.view() !== null;
  }

  /**
   * Whether the Density drawer is open.
   *
   * @returns True while it is.
   */
  public get open(): boolean {
    return this.shown;
  }

  /**
   * Opens or closes the meter; nothing opens it when it is not offered. Closing ends a pick and lets a pin go.
   *
   * @param open Whether it is open.
   */
  public setOpen(open: boolean): void {
    const next = open && this.offered;
    if (next === this.shown) {
      return;
    }
    this.shown = next;
    if (!next) {
      this.waiting = false;
      this.pin = null;
    }
    this.host.notify();
  }

  /**
   * Whether the next press on the map pins the centre.
   *
   * @returns True while a pick waits.
   */
  public get picking(): boolean {
    return this.waiting;
  }

  /**
   * Whether the centre is pinned on the open map rather than following the view.
   *
   * @returns True when it is.
   */
  public get pinned(): boolean {
    return this.pinOnMap() !== null;
  }

  /**
   * The pinned point when it was pinned on the open map.
   *
   * @returns The point, or null.
   */
  private pinOnMap(): DensityPoint | null {
    return this.pin !== null && this.pin.mapId === this.host.lowered().id ? this.pin.point : null;
  }

  /**
   * The view's last settled centre when the page reported it on the open map, so a map switch forgets it.
   *
   * @returns The point, or null.
   */
  private viewCentreOnMap(): DensityPoint | null {
    return this.viewCentre !== null && this.viewCentre.mapId === this.host.lowered().id ? this.viewCentre.point : null;
  }

  /**
   * The point measured: the pin on the open map, else the view's last settled centre on it.
   *
   * @returns The point, or null before the page has reported one on the open map.
   */
  public get centre(): DensityPoint | null {
    return this.pinOnMap() ?? this.viewCentreOnMap();
  }

  /**
   * Starts Measure here: the next press on the map pins the centre. Only while open.
   */
  public startPick(): void {
    if (!this.shown || this.waiting) {
      return;
    }
    this.waiting = true;
    this.host.notify();
  }

  /**
   * Ends Measure here without pinning.
   */
  public cancelPick(): void {
    if (!this.waiting) {
      return;
    }
    this.waiting = false;
    this.host.notify();
  }

  /**
   * Pins the centre at a pressed point and ends the pick; nothing while no pick waits.
   *
   * @param point The point in world pixels.
   */
  public pick(point: DensityPoint): void {
    if (!this.waiting) {
      return;
    }
    this.waiting = false;
    this.pin = { mapId: this.host.lowered().id, point: { x: point.x, y: point.y } };
    this.host.notify();
  }

  /**
   * Follow view: lets the pin go, so the centre follows the view again.
   */
  public follow(): void {
    if (this.pin === null) {
      return;
    }
    this.pin = null;
    this.host.notify();
  }

  /**
   * The view's centre on the open map once the camera has settled. Notifies only while open, following and moved.
   *
   * @param point The centre in world pixels.
   */
  public setViewCentre(point: DensityPoint): void {
    const mapId = this.host.lowered().id;
    const was = this.viewCentre;
    if (was !== null && was.mapId === mapId && was.point.x === point.x && was.point.y === point.y) {
      return;
    }
    this.viewCentre = { mapId, point: { x: point.x, y: point.y } };
    if (this.shown && !this.pinned) {
      this.host.notify();
    }
  }

  /**
   * Binds the host's asset manifest, whose pages and animations the props are counted against.
   *
   * @param manifest The manifest.
   */
  public bindManifest(manifest: AssetManifest): void {
    if (manifest === this.manifest) {
      return;
    }
    this.manifest = manifest;
    if (this.shown) {
      this.host.notify();
    }
  }

  /**
   * The reading at the centre, computed when the map, the centre or the manifest changed since the last one.
   *
   * @returns The reading, or null while closed, unoffered, or before a manifest and a centre.
   */
  public get reading(): DensityReading | null {
    const definition = this.host.definition();
    const view = this.host.view();
    const centre = this.centre;
    const manifest = this.manifest;
    if (!this.shown || definition === null || view === null || centre === null || manifest === null) {
      return null;
    }
    const map = this.host.lowered();
    const memo = this.memo;
    if (memo !== null && memo.map === map && memo.manifest === manifest && memo.centre.x === centre.x && memo.centre.y === centre.y) {
      return memo.reading;
    }
    const reading = measureDensity({ map, tileSize: this.host.tileSize(), chunkSize: this.host.chunkSize(), view: definition.view ?? view, cameraView: view, centre, manifest, cells: this.host.cells, entityTypes: this.host.entityTypes(), definition });
    this.memo = { map, centre, manifest, reading };
    return reading;
  }
}
